import React from 'react';
import { FASTIFY_BASE_URL } from '../config/apiConfig';

/**
 * First-sign-in step for the one-time link in the welcome email.
 *
 * The token is checked (POST /invite/verify), the invitee's email types
 * itself into the field so it's obvious the link did the filling-in, then the
 * password fields open. Submitting sets their password (POST /invite/accept)
 * and hands email + password back to Login, which signs in through its normal
 * flow (MFA setup and all).
 */

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

async function postInvite(path, body) {
  const res = await fetch(`${FASTIFY_BASE_URL}/invite/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data = {};
  try { data = await res.json(); } catch (_) { /* empty body */ }
  return { ok: res.ok, status: res.status, data };
}

/**
 * Mail a fresh setup link — to the address on file, never the caller. Takes
 * { email } (login page "Forgot password") or { token } (expired link). The
 * server answers the same whether or not anything was sent.
 */
export async function requestNewInvite(body) {
  try { await postInvite('resend', body); } catch (_) { /* same message either way */ }
}

/** Types `text` out over ~0.9s; returns [shown, done]. */
function useTypewriter(text) {
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!text) return undefined;
    if (prefersReducedMotion()) { setCount(text.length); return undefined; }
    setCount(0);
    const step = Math.min(45, Math.max(18, 900 / text.length));
    let i = 0;
    let timer;
    // A short beat first so the empty field registers before it fills.
    const start = setTimeout(() => {
      timer = setInterval(() => {
        i += 1;
        setCount(i);
        if (i >= text.length) clearInterval(timer);
      }, step);
    }, 350);
    return () => { clearTimeout(start); clearInterval(timer); };
  }, [text]);
  return [text ? text.slice(0, count) : '', Boolean(text) && count >= text.length];
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5 10.5l3.2 3L15 6.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function useInviteCheck(token) {
  const [state, setState] = React.useState(() => (token ? { phase: 'checking' } : null));
  React.useEffect(() => {
    if (!token) return undefined;
    let live = true;
    postInvite('verify', { token })
      .then(({ ok, status, data }) => {
        if (!live) return;
        if (ok) setState({ phase: 'ready', email: data.email, firstName: data.firstName || '' });
        else setState({ phase: status === 410 ? (data.code === 'INVITE_EXPIRED' ? 'expired' : 'invalid') : 'error', error: data.error });
      })
      .catch(() => { if (live) setState({ phase: 'error' }); });
    return () => { live = false; };
  }, [token]);
  return [state, setState];
}

export default function InviteWelcome({ token, invite, onAccepted, onExit, renderStrength }) {
  const [typed, typedDone] = useTypewriter(invite.phase === 'ready' || invite.phase === 'saving' ? invite.email : '');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [show, setShow] = React.useState(false);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [renewal, setRenewal] = React.useState(null); // null | 'sending' | 'sent'
  const passwordRef = React.useRef(null);

  React.useEffect(() => {
    // Let the password panel finish opening before moving focus into it.
    if (typedDone) {
      const t = setTimeout(() => passwordRef.current?.focus(), prefersReducedMotion() ? 0 : 420);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [typedDone]);

  if (invite.phase === 'checking') {
    return (
      <div className="lg-stack" aria-live="polite">
        <p className="lg-lede lg-invite-lede">Checking your invite…</p>
        <div className="lg-field">
          <span className="lg-label">Email</span>
          <div className="lg-input lg-invite-skeleton" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (invite.phase !== 'ready') {
    const expired = invite.phase === 'expired';
    if (expired && renewal === 'sent') {
      return (
        <div className="lg-stack">
          <p className="lg-lede lg-invite-lede">
            If your account still needs setting up, a new link is on its way to your inbox. It works once and expires in 72&nbsp;hours.
          </p>
          <button type="button" className="lg-btn lg-btn--secondary lg-btn--block" onClick={onExit}>Back to log in</button>
        </div>
      );
    }
    if (expired) {
      const renew = async () => {
        setRenewal('sending');
        await requestNewInvite({ token });
        setRenewal('sent');
      };
      return (
        <div className="lg-stack">
          <p className="lg-lede lg-invite-lede">Setup links expire after 72 hours for your security. We can email you a new one.</p>
          <button type="button" className="lg-btn lg-btn--primary lg-btn--block" onClick={renew} disabled={renewal === 'sending'}>
            {renewal === 'sending' ? 'Sending…' : 'Email me a new link'}
          </button>
          <button type="button" className="lg-link" onClick={onExit}>Back to log in</button>
        </div>
      );
    }
    return (
      <div className="lg-stack">
        <div className="lg-alert lg-alert--note">
          {expired
            ? 'This invite link has expired. Ask your Lodgeic administrator to resend your invite.'
            : invite.phase === 'invalid'
              ? 'This invite link has already been used or is no longer valid. If you’ve already set your password, just log in.'
              : 'We couldn’t check your invite just now. Refresh to try again.'}
        </div>
        <button type="button" className="lg-btn lg-btn--primary lg-btn--block" onClick={onExit}>Go to log in</button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!password) { setError('Choose a password.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setError('');
    setSaving(true);
    try {
      const { ok, data } = await postInvite('accept', { token, password });
      if (!ok) {
        setError(data.error || 'Could not set your password. Try again.');
        setSaving(false);
        return;
      }
      await onAccepted(invite.email, password);
    } catch (_) {
      setError('Could not reach Lodgeic. Check your connection and try again.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="lg-stack">
      <p className="lg-lede lg-invite-lede">
        Your invite is ready. Choose a password to finish setting up your account.
      </p>

      <div className="lg-field">
        <label htmlFor="invite-email" className="lg-label">
          Email
          <span className={`lg-invite-chip${typedDone ? ' is-on' : ''}`} aria-hidden={!typedDone}>
            <CheckIcon /> Filled from your invite
          </span>
        </label>
        <div className="relative">
          {typedDone ? (
            <input
              id="invite-email"
              type="email"
              className="lg-input lg-invite-email is-done"
              value={invite.email}
              readOnly
              autoComplete="username"
            />
          ) : (
            // While typing: a look-alike box with an inline caret (an input
            // can't place a fake caret after proportional text reliably).
            <div id="invite-email" className="lg-input lg-invite-email is-typing" role="textbox" aria-readonly="true" aria-label={`Email, ${invite.email}`}>
              <span>{typed}</span><span className="lg-invite-caret" aria-hidden="true" />
            </div>
          )}
          <span className={`lg-invite-check${typedDone ? ' is-on' : ''}`} aria-hidden="true"><CheckIcon /></span>
        </div>
      </div>

      <div className={`lg-invite-reveal${typedDone ? ' is-open' : ''}`} aria-hidden={!typedDone}>
        <div className="lg-invite-reveal-inner lg-stack">
          <div className="lg-field">
            <label htmlFor="invite-password" className="lg-label">Choose a password</label>
            <div className="relative">
              <input
                id="invite-password"
                ref={passwordRef}
                type={show ? 'text' : 'password'}
                className="lg-input lg-input--trail"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="New password"
                tabIndex={typedDone ? 0 : -1}
              />
              <button type="button" className="lg-eye lg-invite-eye" onClick={() => setShow((s) => !s)} tabIndex={-1}
                aria-label={show ? 'Hide password' : 'Show password'}>
                {show ? 'Hide' : 'Show'}
              </button>
            </div>
            {password && renderStrength ? renderStrength(password) : null}
          </div>
          <div className="lg-field">
            <label htmlFor="invite-confirm" className="lg-label">Confirm password</label>
            <input
              id="invite-confirm"
              type={show ? 'text' : 'password'}
              className="lg-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder="Type it again"
              tabIndex={typedDone ? 0 : -1}
            />
          </div>
          {error ? <div className="lg-alert lg-alert--error" role="alert">{error}</div> : null}
          <button type="submit" className="lg-btn lg-btn--primary lg-btn--block" disabled={saving || !typedDone} tabIndex={typedDone ? 0 : -1}>
            {saving ? 'Setting up your account…' : 'Set password and sign in'}
          </button>
        </div>
      </div>
    </form>
  );
}
